/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：webpack chunk 加载失败时的全局重试/恢复机制。修补 UmiJS 4 缺失的原生 chunkRetry 配置。
 * - English: Global retry/recovery for webpack chunk loading failures. Patches the missing native chunkRetry in UmiJS 4.
 *
 * 调用关系 / Call Flow
 * - 中文：由 src/app.ts 在最早期导入（先于任何动态 import），在 window 上注册 error / unhandledrejection 监听器。
 * - English: Imported early by src/app.ts before any dynamic import, registers window error / unhandledrejection listeners.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧，不访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Browser-only; no database access, no SQL, no server-side fractal-core algorithm replication.
 *
 * 上线注意 / Release Notes
 * - 中文：该模块在 dist 生产构建中生效；验证方法：用 Chrome DevTools 阻止任意 .async.js 请求后观察页面是否自动恢复。
 * - English: Active in dist production builds; verify by blocking any .async.js request in Chrome DevTools and observing auto-recovery.
 */

const CHUNK_LOAD_ERROR_RE = /Loading (?:CSS )?chunk \S+ failed/i;
const MAX_AUTO_RELOADS = 2;
const RELOAD_COOLDOWN_MS = 5000;

let autoReloadCount = 0;
let lastReloadTime = 0;

function handleChunkError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  const detail: { message?: string } =
    event instanceof ErrorEvent
      ? (event as ErrorEvent)
      : { message: (event as PromiseRejectionEvent).reason?.message || '' };

  const msg = detail.message || '';

  if (!CHUNK_LOAD_ERROR_RE.test(msg)) {
    return false; // not a chunk error, let other handlers deal with it
  }

  event.preventDefault();

  const now = Date.now();
  if (now - lastReloadTime < RELOAD_COOLDOWN_MS) {
    // Too soon since last reload — likely a persistent issue, don't infinite-loop
    console.warn('[ChunkRetry] Cooldown active, suppressing auto-reload. Refresh the page manually if the problem persists.');
    return true;
  }

  if (autoReloadCount >= MAX_AUTO_RELOADS) {
    console.warn('[ChunkRetry] Max auto-reloads reached. Please refresh the page manually.');
    return true;
  }

  autoReloadCount += 1;
  lastReloadTime = now;

  console.warn(
    `[ChunkRetry] Chunk load failed (${msg}). Auto-reloading (attempt ${autoReloadCount}/${MAX_AUTO_RELOADS})...`,
  );

  window.location.reload();
  return true;
}

export function initChunkRetry(): void {
  if (typeof window === 'undefined') return;

  // Catch unhandled promise rejections from dynamic import()
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    handleChunkError(event);
  });

  // Catch script-level errors from <script> tag onerror
  window.addEventListener('error', (event: ErrorEvent) => {
    handleChunkError(event);
  });
}
